'use client';

import { useState } from 'react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { SUBSCRIPTION_PLANS } from '@/stores/subscriptionStore';
import { PricingCard, FeatureComparison } from './PricingCard';
import { Card, CardContent, Badge } from '@/components/ui';
import type { SubscriptionPlan, BillingInterval } from '@/types/subscription';

interface PlanSelectorProps {
  onSelectPlan?: (plan: SubscriptionPlan, interval: BillingInterval) => void;
  showComparison?: boolean;
  highlightCurrent?: boolean;
  excludeFree?: boolean;
}

export function PlanSelector({
  onSelectPlan,
  showComparison = false,
  highlightCurrent = true,
  excludeFree = false,
}: PlanSelectorProps) {
  const { tier: currentTier } = useSubscription();
  const [interval, setInterval] = useState<BillingInterval>('monthly');

  const plans = excludeFree
    ? SUBSCRIPTION_PLANS.filter((p) => p.tier !== 'free')
    : SUBSCRIPTION_PLANS;

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    onSelectPlan?.(plan, interval);
  };

  return (
    <div className="space-y-8">
      {/* Billing interval toggle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-3 rounded-full bg-secondary-100 dark:bg-secondary-800 p-1">
          <button
            onClick={() => setInterval('monthly')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
              interval === 'monthly'
                ? 'bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 shadow-sm'
                : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setInterval('annual')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-all flex items-center gap-2 ${
              interval === 'annual'
                ? 'bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 shadow-sm'
                : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
            }`}
          >
            Annual
            <Badge color="green" size="sm">
              Save 20%
            </Badge>
          </button>
        </div>
      </div>

      {/* Plan cards grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {plans.map((plan) => (
          <PricingCard
            key={plan.tier}
            plan={plan}
            interval={interval}
            isCurrentPlan={highlightCurrent && plan.tier === currentTier}
            onSelect={handleSelectPlan}
          />
        ))}
      </div>

      {/* Feature comparison table */}
      {showComparison && (
        <Card className="mt-12">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-6">
              Compare All Features
            </h3>
            <FeatureComparison plans={plans} interval={interval} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Simple plan selection for signup flow
interface SimplePlanSelectorProps {
  selectedTier?: string;
  onSelect: (tier: string) => void;
  interval: BillingInterval;
  onIntervalChange: (interval: BillingInterval) => void;
}

export function SimplePlanSelector({
  selectedTier,
  onSelect,
  interval,
  onIntervalChange,
}: SimplePlanSelectorProps) {
  return (
    <div className="space-y-6">
      {/* Billing interval toggle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-1 rounded-lg bg-secondary-100 dark:bg-secondary-800 p-1">
          <button
            onClick={() => onIntervalChange('monthly')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              interval === 'monthly'
                ? 'bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 shadow-sm'
                : 'text-secondary-600 dark:text-secondary-400'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => onIntervalChange('annual')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              interval === 'annual'
                ? 'bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 shadow-sm'
                : 'text-secondary-600 dark:text-secondary-400'
            }`}
          >
            Annual (-20%)
          </button>
        </div>
      </div>

      {/* Plan options */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {SUBSCRIPTION_PLANS.map((plan) => {
          const price = interval === 'annual' ? plan.pricing.annual : plan.pricing.monthly;
          const isSelected = selectedTier === plan.tier;

          return (
            <button
              key={plan.tier}
              onClick={() => onSelect(plan.tier)}
              className={`relative flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-all ${
                isSelected
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                  : 'border-secondary-200 dark:border-secondary-700 hover:border-secondary-300 dark:hover:border-secondary-600'
              }`}
            >
              {/* Radio indicator */}
              <div
                className={`mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 transition-all ${
                  isSelected
                    ? 'border-primary-500 bg-primary-500'
                    : 'border-secondary-300 dark:border-secondary-600'
                }`}
              >
                {isSelected && (
                  <div className="flex h-full items-center justify-center">
                    <div className="h-2 w-2 rounded-full bg-white" />
                  </div>
                )}
              </div>

              {/* Plan info */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-secondary-900 dark:text-secondary-100">
                    {plan.name}
                  </span>
                  {plan.popular && (
                    <Badge color="blue" size="sm">
                      Popular
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 text-sm text-secondary-500 dark:text-secondary-400">
                  {plan.description}
                </p>
                <p className="mt-2 font-semibold text-secondary-900 dark:text-secondary-100">
                  ${price}
                  <span className="font-normal text-secondary-500 dark:text-secondary-400">/mo</span>
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
